import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCheckout,
  createPortal,
  getWorkspaceSubscription,
  listPlans,
} from "@/lib/api/billing";

export const billingKeys = {
  subscription: (workspaceId: string) => ["billing", "subscription", workspaceId] as const,
  plans: (workspaceId: string) => ["billing", "plans", workspaceId] as const,
};

/** The business's subscription row, or null on the trial and after it. */
export function useWorkspaceSubscription(workspaceId: string | undefined) {
  return useQuery({
    queryKey: billingKeys.subscription(workspaceId ?? ""),
    queryFn: () => getWorkspaceSubscription(workspaceId!),
    enabled: Boolean(workspaceId),
  });
}

export function usePlans(workspaceId: string | undefined) {
  return useQuery({
    queryKey: billingKeys.plans(workspaceId ?? ""),
    queryFn: () => listPlans(workspaceId),
    enabled: Boolean(workspaceId),
  });
}

/** Resolves with the Checkout URL; the page redirects the browser there. */
export function useCheckout() {
  return useMutation({ mutationFn: createCheckout });
}

export function usePortal() {
  return useMutation({ mutationFn: createPortal });
}

/** After Checkout: the subscription, and the plans (Plus on one business discounts the others). */
export function useInvalidateSubscription() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["billing"] });
}
